/* Houndkeep "Buy more, save more" (snippets/hk-qty-offer.liquid).
   - "Buy 1": nothing changes, Dawn's product form adds the selected variant as usual.
   - "Buy 2" / "Buy 3": a click on Dawn's Add to cart button (#ProductSubmitButton-<section>; the sticky add-to-cart bar clicks
     that same button) is taken over: one line per chosen variant is written into this block's form, which the shared
     handler in houndkeep-rewards.js (form[data-hk-add-form]) sends to /cart/add.js and shows in the cart drawer.
   - Items follow the variant picked in Dawn's picker until the shopper changes an item by hand.
   - The shown total is price x items minus the saving, with the saving rounded down per item (what Shopify charged in
     a live cart on 2026-10-05); the real discount is applied by Shopify's automatic discounts in the cart. Nothing is
     invented here: prices come from the variants, the percentages from the theme settings rendered into the markup. */
(() => {
  if (customElements.get('hk-qty-offer')) return;

  const lineTotals = (lines, pct) => {
    // group by variant like buildAddPayload does (one cart line per variant)
    const byId = new Map();
    lines.forEach((l) => byId.set(l.id, { price: l.price, qty: (byId.get(l.id)?.qty || 0) + 1 }));
    let was = 0;
    let now = 0;
    byId.forEach(({ price, qty }) => {
      const line = price * qty;
      was += line;
      now += line - Math.floor((price * pct) / 100) * qty;
    });
    return { was, now };
  };

  customElements.define('hk-qty-offer', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      try { this.data = JSON.parse(this.querySelector('[data-hk-qty-data]').textContent); } catch (e) { return; }
      this.variants = new Map(this.data.variants.map((v) => [String(v.id), v]));
      this.section = this.dataset.section;
      this.product = this.closest('product-info') || document;
      this.form = this.querySelector('form[data-hk-add-form]');
      this.options = [...this.querySelectorAll('[data-hk-option]')];
      // Shopify's own price text for the default variant (exact money format, currency code, converted currency):
      // shown again whenever every item is that variant, so the script only formats prices Liquid could not know.
      this.serverPrices = new Map(this.options.map((opt) => [opt, {
        now: opt.querySelector('[data-hk-now]')?.innerHTML,
        was: opt.querySelector('[data-hk-was]')?.innerHTML,
      }]));
      this.querySelectorAll('[data-hk-js-only]').forEach((el) => { el.disabled = false; el.removeAttribute('data-hk-js-only'); });
      this.querySelectorAll('[data-hk-visual]').forEach((picker) => {
        picker.hidden = false;
        picker.closest('.hk-qty__item').classList.add('has-visual-picker');
      });

      this.addEventListener('click', (e) => {
        const choice = e.target.closest('[data-hk-variant-choice]');
        if (!choice || choice.disabled) return;
        const item = choice.closest('.hk-qty__item');
        const select = item.querySelector('[data-hk-item]');
        select.value = choice.dataset.hkVariantChoice;
        select.dataset.hkTouched = '1';
        item.querySelector('details').open = false;
        this.update();
        item.querySelector('summary').focus({ preventScroll: true });
      });
      this.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape') return;
        const picker = e.target.closest('[data-hk-visual]');
        if (!picker?.open) return;
        picker.open = false;
        picker.querySelector('summary').focus({ preventScroll: true });
        e.stopPropagation();
      });

      this.addEventListener('change', (e) => {
        if (e.target.matches('.hk-qty__radio')) this.choose(Number(e.target.value));
        if (e.target.matches('[data-hk-item]')) { e.target.dataset.hkTouched = '1'; this.update(); }
        e.stopPropagation(); // Dawn's product-info treats every bubbling change as a variant pick
      });

      // Follow Dawn's selected variant (its hidden input[name=id] in the product form)
      this.onVariant = () => this.follow();
      this.product.addEventListener('change', this.onVariant);
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
        this.unsub = subscribe(PUB_SUB_EVENTS.variantChange, () => window.setTimeout(this.onVariant, 0));
      }

      // Take over Dawn's Add to cart for Buy 2 / Buy 3 (capture: before Dawn's form submits)
      this.onClick = (e) => {
        this.querySelectorAll('[data-hk-visual][open]').forEach((picker) => {
          if (!picker.contains(e.target)) picker.open = false;
        });
        const button = e.target.closest && e.target.closest(`#ProductSubmitButton-${CSS.escape(this.section)}`);
        if (!button || this.qty() < 2) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        // The sticky add-to-cart bar (phones and desktop) clicks this same button and marks it; focus then returns to the bar after the drawer,
        // not to the main button far up the page (that scrolled the page back up; review 2026-10-06).
        const sticky = button.dataset.hkFromSticky ? document.getElementById(button.dataset.hkFromSticky) : null;
        delete button.dataset.hkFromSticky;
        if (button.getAttribute('aria-disabled') === 'true' || this.form.getAttribute('data-hk-busy') === 'true') return;
        this.form.setAttribute('data-hk-return-focus', sticky ? sticky.id : button.id);
        this.submit(button);
      };
      document.addEventListener('click', this.onClick, true);

      // Mirror the shared handler's busy state on Dawn's button, so the shopper sees it working
      this.busyObserver = new MutationObserver(() => {
        const busy = this.form.getAttribute('data-hk-busy') === 'true';
        const button = this.primary();
        if (!button) return;
        button.classList.toggle('loading', busy);
        button.querySelector('.loading__spinner')?.classList.toggle('hidden', !busy);
        if (busy) button.setAttribute('aria-disabled', 'true'); else if (!this.soldOut) button.removeAttribute('aria-disabled');
      });
      this.busyObserver.observe(this.form, { attributes: true, attributeFilter: ['data-hk-busy'] });

      this.follow();
      // Start from the radio that is really checked: a browser can restore "Buy 2/3" after a reload or back/forward,
      // and showing "Buy 1" while Add to cart adds 2 or 3 items is wrong (review 2026-10-06).
      this.choose(this.qty());
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onClick, true);
      this.product?.removeEventListener('change', this.onVariant);
      this.unsub?.();
      this.busyObserver?.disconnect();
      this.ready = false;
    }

    primary() { return document.getElementById(`ProductSubmitButton-${this.section}`); }
    qty() { return Number(this.querySelector('.hk-qty__radio:checked')?.value || 1); }
    current() {
      const input = this.product.querySelector('form[action*="/cart/add"] input[name="id"]') || document.querySelector(`#product-form-${this.section} input[name="id"]`);
      return input ? String(input.value) : String(this.data.variants.find((v) => v.available)?.id || this.data.variants[0].id);
    }

    choose(n) {
      this.options.forEach((opt) => {
        const on = Number(opt.dataset.hkOption) === n;
        opt.classList.toggle('is-active', on);
        const items = opt.querySelector('[data-hk-items]');
        if (items) items.hidden = !on;
      });
      this.classList.toggle('is-multi', n > 1);
      this.update();
    }

    follow() {
      const id = this.current();
      this.querySelectorAll('[data-hk-item]').forEach((sel) => {
        if (sel.dataset.hkTouched) return;
        const opt = [...sel.options].find((o) => o.value === id && !o.disabled);
        if (opt) sel.value = id;
      });
      this.update();
    }

    lines(n) {
      const opt = this.options.find((o) => Number(o.dataset.hkOption) === n);
      const selects = opt ? [...opt.querySelectorAll('[data-hk-item]')] : [];
      const ids = selects.length ? selects.map((s) => s.value) : Array.from({ length: n }, () => this.current());
      return ids.map((id) => this.variants.get(String(id))).filter(Boolean);
    }

    money(cents) {
      const api = window.HoundkeepRewards;
      let text = api && api.formatMoney
        ? api.formatMoney(cents, this.dataset.hkMoneyFormat, this.dataset.hkCurrency, this.dataset.hkLocale)
        : (cents / 100).toFixed(2);
      // In a converted (Markets) currency there is no money format to use, so the price is built with Intl and has no
      // currency code, while Liquid's money_with_currency next to it does ("€62,90 EUR"). With "Show currency codes" on,
      // add the code so the two match (review 2026-10-06).
      const code = this.dataset.hkCode;
      if (code && !this.dataset.hkMoneyFormat && !text.includes(code)) text = `${text} ${code}`;
      return text;
    }

    update() {
      this.querySelectorAll('.hk-qty__item').forEach((item) => {
        const variant = this.variants.get(item.querySelector('[data-hk-item]')?.value);
        if (!variant) return;
        const image = item.querySelector('[data-hk-choice-image]');
        if (!image) return;
        image.hidden = !variant.image;
        if (variant.image && image.getAttribute('src') !== variant.image) image.setAttribute('src', variant.image);
        if (!variant.image) image.removeAttribute('src');
        item.querySelector('[data-hk-choice-title]').textContent = variant.title;
        const summary = item.querySelector('summary');
        summary.setAttribute('aria-label', `${summary.dataset.hkItemLabel}: ${variant.title}`);
        item.querySelectorAll('[data-hk-variant-choice]').forEach((choice) => {
          choice.setAttribute('aria-pressed', String(choice.dataset.hkVariantChoice === String(variant.id)));
        });
      });
      this.options.forEach((opt) => {
        const n = Number(opt.dataset.hkOption);
        const pct = Number(opt.dataset.hkPercent) || 0;
        const lines = this.lines(n);
        if (lines.length !== n) return;
        const nowEl = opt.querySelector('[data-hk-now]');
        const wasEl = opt.querySelector('[data-hk-was]');
        const server = this.serverPrices.get(opt);
        if (server && lines.every((v) => String(v.id) === this.dataset.hkDefault)) {
          if (nowEl && server.now != null) nowEl.innerHTML = server.now;
          if (wasEl && server.was != null) wasEl.innerHTML = server.was;
          return;
        }
        const { was, now } = lineTotals(lines.map((v) => ({ id: v.id, price: v.price })), pct);
        if (nowEl) nowEl.textContent = this.money(now);
        if (wasEl) { const sr = wasEl.querySelector('.visually-hidden'); wasEl.textContent = this.money(was); if (sr) wasEl.prepend(sr); }
      });
      const n = this.qty();
      const lines = this.lines(n);
      this.soldOut = n > 1 && lines.some((v) => !v.available);
      const button = this.primary();
      if (!button) return;
      if (this.soldOut) {
        button.setAttribute('aria-disabled', 'true');
        this.disabledByUs = true;
      } else if (this.disabledByUs && this.form.getAttribute('data-hk-busy') !== 'true') {
        // Only undo what this block set (also when going back to Buy 1); Dawn's own sold-out state uses `disabled`.
        button.removeAttribute('aria-disabled');
        this.disabledByUs = false;
      }
    }

    submit(button) {
      const n = this.qty();
      const lines = this.lines(n);
      if (lines.length !== n || lines.some((v) => !v.available)) return;
      const holder = this.form.querySelector('[data-hk-lines]');
      holder.textContent = '';
      lines.forEach((v) => {
        const line = document.createElement('div');
        line.setAttribute('data-hk-line', '');
        line.setAttribute('data-hk-title', v.title && v.title !== 'Default Title' ? `${this.data.product} – ${v.title}` : this.data.product);
        const id = document.createElement('input');
        id.type = 'hidden'; id.value = String(v.id); id.setAttribute('data-hk-variant-input', '');
        const q = document.createElement('input');
        q.type = 'hidden'; q.value = '1'; q.setAttribute('data-hk-qty-input', '');
        line.append(id, q);
        holder.append(line);
      });
      const submitter = this.form.querySelector('.hk-qty__submit');
      if (typeof this.form.requestSubmit === 'function') this.form.requestSubmit(submitter);
      else this.form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
      this.lastButton = button;
    }
  });
})();
